/**
 * HTML for the admin video preview SweetAlert (player + act details).
 *
 * @see docs/APP_UI_SPEC.md — C4
 */

import { sanitizeSocialUsername } from './socialLinks.js';

export const videoPreviewPopupWidth = '900px';

function escapeHtml(text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function detailRow(label, valueHtml) {
  return `<div class="video-preview-detail-row"><span class="label">${escapeHtml(label)}:</span> <span class="value">${valueHtml}</span></div>`;
}

function buildDetailsHtml(application) {
  const rows = [];

  if (application.email) {
    rows.push(detailRow('Email', escapeHtml(application.email)));
  }

  const facebook = application.facebook?.trim();
  if (facebook) {
    const q = encodeURIComponent(facebook);
    rows.push(
      detailRow(
        'Facebook',
        `<a href="https://www.facebook.com/search/people/?q=${q}" target="_blank" rel="noopener">${escapeHtml(facebook)}</a>`
      )
    );
  }

  const instagram = sanitizeSocialUsername(application.instagram);
  if (instagram) {
    rows.push(
      detailRow(
        'Instagram',
        `<a href="https://instagram.com/${encodeURIComponent(instagram)}" target="_blank" rel="noopener">${escapeHtml(instagram)}</a>`
      )
    );
  }

  if (application.notes?.trim()) {
    rows.push(detailRow('Notes', escapeHtml(application.notes)));
  }

  if (rows.length === 0) {
    return '<p class="video-preview-empty">No contact details on this application.</p>';
  }

  return rows.join('');
}

/**
 * Build SweetAlert HTML: video left, act details right (stacks on narrow widths).
 *
 * @param {{ comedian_name?: string, video_link?: string, email?: string, facebook?: string, instagram?: string, notes?: string }} application
 * @param {string} embedUrl
 * @returns {string}
 */
export function buildVideoPreviewHtml(application, embedUrl) {
  const videoHref = escapeHtml(application.video_link || '');
  const safeEmbed = escapeHtml(embedUrl);
  const name = application.comedian_name?.trim()
    ? `<p class="video-preview-name" style="margin:0 0 10px;font-weight:600;">${escapeHtml(application.comedian_name)}</p>`
    : '';

  return `
<div class="video-preview-layout" style="display:flex;gap:16px;flex-wrap:wrap;text-align:left;align-items:flex-start;">
  <div class="video-preview-player" style="flex:1 1 320px;min-width:260px;">
    <iframe
      src="${safeEmbed}"
      style="width:100%;aspect-ratio:16/9;border:none;border-radius:6px;"
      allow="autoplay; encrypted-media; fullscreen"
      allowfullscreen
    ></iframe>
    <p style="margin:8px 0 0;font-size:0.9em;">
      <a href="${videoHref}" target="_blank" rel="noopener">Open video in new tab</a>
    </p>
  </div>
  <div class="video-preview-details" style="flex:1 1 220px;min-width:200px;font-size:0.95em;line-height:1.45;">
    ${name}
    ${buildDetailsHtml(application)}
  </div>
</div>`.trim();
}
